/**
 * Skip Link Component
 * Accessibility feature to skip to main content
 * Visible only when focused (keyboard navigation)
 */

import { Box } from '@mui/material';

interface SkipLinkProps {
  targetId?: string;
}

/**
 * Skip Link Component
 * Allows keyboard users to skip navigation and jump to main content
 * Hidden by default, visible on focus
 */
export const SkipLink: React.FC<SkipLinkProps> = ({ targetId = 'main-content' }) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      component="a"
      href={`#${targetId}`}
      onClick={handleClick}
      sx={{
        position: 'absolute',
        top: -1000,
        left: -1000,
        height: 1,
        width: 1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        '&:focus': {
          position: 'fixed',
          top: 8,
          left: 8,
          height: 'auto',
          width: 'auto',
          overflow: 'visible',
          clip: 'auto',
          whiteSpace: 'normal',
          zIndex: 9999,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          px: 2,
          py: 1,
          borderRadius: 1,
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          boxShadow: 4,
          outline: '3px solid',
          outlineColor: 'primary.light',
          outlineOffset: 2,
        },
      }}
    >
      Skip to main content
    </Box>
  );
};
